import React from 'react';
import { motion } from 'framer-motion';
import { Icons } from './Icons';
import NilVisionLogo from './NilVisionLogo';
import UserAvatar from './UserAvatar';
import { SIDEBAR_NAV } from '../config/nav';

const BRAND   = '#5A38FD';
const BRAND_L = '#a78bfa';
const SANS    = '"Geist", "Inter", system-ui, sans-serif';

const NAV_ITEM = (active) => ({
  display: 'flex', alignItems: 'center', gap: 10,
  padding: '10px 12px', borderRadius: 10,
  fontSize: '0.875rem', fontWeight: active ? 600 : 500,
  color: active ? '#fff' : 'rgba(255,255,255,0.45)',
  cursor: 'pointer', border: 'none', width: '100%', textAlign: 'left',
  background: active ? 'rgba(90,56,253,0.14)' : 'transparent',
  fontFamily: SANS, transition: 'color 160ms, background 160ms',
  boxShadow: active ? 'inset 0 0 0 1px rgba(90,56,253,0.2)' : 'none',
});

export default function Sidebar({ activeComponent, onNavigate, profile, signOut, ghostDraftPending, unreadCount, onNotifOpen }) {
  return (
    <>
      <style>{`
        .nv-sidebar { display: none; }
        @media (min-width: 768px) { .nv-sidebar { display: flex; } }
        .nv-sidebar-nav-item:hover { background: rgba(255,255,255,0.04) !important; color: rgba(255,255,255,0.75) !important; }
        .nv-sidebar-nav-item.active:hover { background: rgba(90,56,253,0.18) !important; }
      `}</style>

      <aside className="nv-sidebar" style={{
        position: 'fixed', top: 0, left: 0, bottom: 0, width: 220, zIndex: 40,
        flexDirection: 'column',
        background: 'rgba(8,8,13,0.94)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
        borderRight: '1px solid rgba(255,255,255,0.06)',
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '20px 16px 16px', borderBottom: '1px solid rgba(255,255,255,0.05)', flexShrink: 0 }}>
          <NilVisionLogo size={28} showWordmark={false} />
          <span style={{
            fontSize: '0.9rem', fontWeight: 800, letterSpacing: '0.08em',
            background: 'linear-gradient(135deg, #fff 0%, #c4b5fd 55%, #93c5fd 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
          }}>NILVISION</span>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '12px 8px', display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto' }}>
          {SIDEBAR_NAV.map((item) => {
            const IconComponent = Icons[item.icon] || Icons.Home;
            const active  = activeComponent === item.id;
            const showDot = item.id === 'ghost' && ghostDraftPending && !active;
            return (
              <motion.button key={item.id} whileTap={{ scale: 0.97 }}
                onClick={() => onNavigate(item.id)}
                className={`nv-sidebar-nav-item${active ? ' active' : ''}`}
                style={NAV_ITEM(active)}>
                <div style={{ position: 'relative', flexShrink: 0, display: 'flex' }}>
                  <IconComponent style={{ width: 18, height: 18 }} />
                  {showDot && (
                    <motion.div animate={{ scale: [1, 1.2, 1], opacity: [0.8, 1, 0.8] }} transition={{ repeat: Infinity, duration: 2 }}
                      style={{ position: 'absolute', top: -3, right: -4, width: 7, height: 7, borderRadius: '50%', background: BRAND_L, boxShadow: `0 0 0 2px #08080D, 0 0 6px rgba(167,139,250,0.7)` }} />
                  )}
                </div>
                <span>{item.label}</span>
              </motion.button>
            );
          })}
        </nav>

        {/* Footer */}
        <div style={{ padding: '10px 8px', borderTop: '1px solid rgba(255,255,255,0.05)', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <motion.button whileTap={{ scale: 0.97 }}
            onClick={() => onNavigate('profile')}
            className={`nv-sidebar-nav-item${activeComponent === 'profile' ? ' active' : ''}`}
            style={NAV_ITEM(activeComponent === 'profile')}>
            <UserAvatar profile={profile} size={20} />
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {profile?.first_name || 'Profile'}
            </span>
          </motion.button>

          {profile?.is_admin && (
            <motion.button whileTap={{ scale: 0.97 }}
              onClick={() => { globalThis.location.href = '/admin'; }}
              className="nv-sidebar-nav-item"
              style={{ ...NAV_ITEM(false), color: BRAND_L, fontSize: '0.8rem' }}>
              <Icons.Crown style={{ width: 16, height: 16 }} />
              <span>Admin</span>
            </motion.button>
          )}

          <motion.button whileTap={{ scale: 0.97 }}
            onClick={onNotifOpen}
            className="nv-sidebar-nav-item"
            style={{ ...NAV_ITEM(false), position: 'relative' }}>
            <div style={{ position: 'relative', display: 'flex', flexShrink: 0 }}>
              <Icons.Bell style={{ width: 16, height: 16 }} />
              {unreadCount > 0 && (
                <div style={{
                  position: 'absolute', top: -4, right: -5,
                  minWidth: 14, height: 14, borderRadius: 7,
                  background: BRAND, border: '2px solid #08080D',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 8, fontWeight: 700, color: '#fff', padding: '0 2px',
                  boxShadow: '0 0 8px rgba(90,56,253,0.7)',
                }}>
                  {unreadCount > 9 ? '9+' : unreadCount}
                </div>
              )}
            </div>
            <span>Notifications</span>
          </motion.button>

          <motion.button whileTap={{ scale: 0.97 }}
            onClick={signOut}
            className="nv-sidebar-nav-item"
            style={{ ...NAV_ITEM(false), color: 'rgba(255,255,255,0.22)', fontSize: '0.8rem' }}>
            <Icons.Logout style={{ width: 16, height: 16, opacity: 0.5 }} />
            <span>Sign Out</span>
          </motion.button>
        </div>
      </aside>
    </>
  );
}
